/**
 * Страница аудита и логов изменений
 * Просмотр истории всех действий в админ-панели
 */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FileText,
  Filter,
  Search,
  Download,
  Trash2,
  Calendar,
  User,
  Activity,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Eye,
  X,
  ExternalLink,
  Plus,
  Edit
} from 'lucide-react'
import { format, subDays, startOfDay, endOfDay } from 'date-fns'
import { ru } from 'date-fns/locale'
import { 
  getActionDescription
} from '../utils/auditLogger'
import { secureApiService } from '../../services/apiService'

// Функция маппинга серверных логов в формат UI
const mapServerLogToUi = (log) => ({
  id: log.id,
  timestamp: new Date(log.created_at).getTime(),
  formattedTime: format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: ru }),
  user: 'system', // временно, пока сервер не передает user
  action: log.action_type,
  details: log.details || {},
  userAgent: log.user_agent || log.details?.userAgent || ''
})

// Компонент записи лога
const LogEntry = ({ log, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const getActionColor = (action) => {
    if (action.includes('CREATE')) return 'text-green-600 bg-green-50 border-green-200'
    if (action.includes('UPDATE')) return 'text-blue-600 bg-blue-50 border-blue-200'
    if (action.includes('DELETE')) return 'text-red-600 bg-red-50 border-red-200'
    if (action.includes('LOGIN') || action.includes('LOGOUT')) return 'text-purple-600 bg-purple-50 border-purple-200'
    if (action.includes('EXPORT') || action.includes('IMPORT')) return 'text-orange-600 bg-orange-50 border-orange-200'
    return 'text-gray-600 bg-gray-50 border-gray-200'
  }

  const getActionIcon = (action) => {
    if (action.includes('CREATE')) return <Plus className="w-3 h-3" />
    if (action.includes('UPDATE')) return <Edit className="w-3 h-3" />
    if (action.includes('DELETE')) return <Trash2 className="w-3 h-3" />
    if (action.includes('LOGIN') || action.includes('LOGOUT')) return <User className="w-3 h-3" />
    if (action.includes('EXPORT') || action.includes('IMPORT')) return <ExternalLink className="w-3 h-3" />
    return <Activity className="w-3 h-3" />
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border rounded-lg p-4 hover:shadow-sm transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <span className={`px-2 py-1 text-xs font-medium border rounded-full flex items-center space-x-1 ${getActionColor(log.action)}`}>
              {getActionIcon(log.action)}
              <span>{log.action}</span>
            </span>
            
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{log.formattedTime}</span>
            </div>

            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <User className="w-3 h-3" />
              <span>{log.user || 'system'}</span>
            </div>
          </div>

          <p className="text-gray-900 mb-2">
            {getActionDescription(log.action, log.details || {})}
          </p>

          {Object.keys(log.details || {}).length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span>Детали</span>
            </button>
          )}
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={() => onViewDetails(log)}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mt-3"
          >
            <div className="bg-gray-50 p-3 rounded-lg">
              <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Модальное окно с деталями лога
const LogDetailsModal = ({ log, isOpen, onClose }) => {
  if (!isOpen || !log) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Детали записи лога</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Действие
                </label>
                <div className="p-2 bg-gray-50 rounded text-sm">{log.action}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Пользователь
                </label>
                <div className="p-2 bg-gray-50 rounded text-sm">{log.user || 'system'}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Время
                </label>
                <div className="p-2 bg-gray-50 rounded text-sm">{log.formattedTime}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID записи
                </label>
                <div className="p-2 bg-gray-50 rounded text-sm font-mono">{log.id}</div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Описание
              </label>
              <div className="p-2 bg-gray-50 rounded text-sm">
                {getActionDescription(log.action, log.details || {})}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Детали (JSON)
              </label>
              <div className="p-3 bg-gray-900 text-green-400 rounded text-xs overflow-auto max-h-64">
                <pre>{JSON.stringify(log, null, 2)}</pre>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Agent
              </label>
              <div className="p-2 bg-gray-50 rounded text-xs text-gray-600 break-all">
                {log.userAgent || 'Не указан'}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Закрыть
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

// Основной компонент страницы
const AuditPage = () => {
  const [logs, setLogs] = useState([])
  const [filteredLogs, setFilteredLogs] = useState([])
  const [loading, setLoading] = useState(true)

  // Фильтры
  const [searchTerm, setSearchTerm] = useState('')
  const [actionFilter, setActionFilter] = useState('all')
  const [dateRange, setDateRange] = useState('all')
  const [userFilter, setUserFilter] = useState('all')

  // Модальное окно
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, log: null })

  // Статистика
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    creates: 0,
    updates: 0,
    deletes: 0
  })

  // Загрузка данных с серверного API
  const loadLogs = async () => {
    try {
      setLoading(true)
      
      // Получаем логи с сервера
      const response = await secureApiService.request('/audit?limit=500', { method: 'GET' })
      const serverLogs = response.data?.logs || response.logs || []
      
      // Маппим серверные логи в формат UI
      const mappedLogs = serverLogs.map(mapServerLogToUi)
      setLogs(mappedLogs)
      
      // Подсчет статистики
      const now = new Date()
      const todayStart = startOfDay(now)
      const weekStart = subDays(todayStart, 7)

      const newStats = {
        total: mappedLogs.length,
        today: mappedLogs.filter(log => log.timestamp >= todayStart.getTime()).length,
        thisWeek: mappedLogs.filter(log => log.timestamp >= weekStart.getTime()).length,
        creates: mappedLogs.filter(log => log.action.includes('CREATE')).length,
        updates: mappedLogs.filter(log => log.action.includes('UPDATE')).length,
        deletes: mappedLogs.filter(log => log.action.includes('DELETE')).length
      }
      setStats(newStats)
    } catch (error) {
      console.error('Ошибка загрузки логов:', error)
      // Fallback к пустому массиву при ошибке
      setLogs([])
      setStats({ total: 0, today: 0, thisWeek: 0, creates: 0, updates: 0, deletes: 0 })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLogs()
  }, [])

  // Применение фильтров
  useEffect(() => {
    let filtered = [...logs]

    // Поиск
    if (searchTerm) {
      filtered = filtered.filter(log => 
        getActionDescription(log.action, log.details).toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Фильтр по действию
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter)
    }

    // Фильтр по дате
    if (dateRange !== 'all') {
      const now = new Date()
      let startDate, endDate

      switch (dateRange) {
        case 'today':
          startDate = startOfDay(now)
          endDate = endOfDay(now)
          break
        case 'week':
          startDate = subDays(startOfDay(now), 7)
          endDate = endOfDay(now)
          break
        case 'month':
          startDate = subDays(startOfDay(now), 30)
          endDate = endOfDay(now)
          break
        default:
          startDate = new Date(0)
          endDate = now
      }

      filtered = filtered.filter(log => {
        const logDate = new Date(log.timestamp)
        return logDate >= startDate && logDate <= endDate
      })
    }

    // Фильтр по пользователю
    if (userFilter !== 'all') {
      filtered = filtered.filter(log => log.user === userFilter)
    }

    setFilteredLogs(filtered)
  }, [logs, searchTerm, actionFilter, dateRange, userFilter])

  // Получение уникальных пользователей и действий
  const uniqueUsers = [...new Set(logs.map(log => log.user))]
  const uniqueActions = [...new Set(logs.map(log => log.action))]

  // Обработчики
  const handleExportCSV = () => {
    try {
      // Формируем CSV из серверных данных
      const headers = ['Время', 'Действие', 'Пользователь', 'Описание', 'Детали']
      const csvRows = [
        headers.join(','),
        ...logs.map(log => [
          `"${log.formattedTime}"`,
          `"${log.action}"`,
          `"${log.user}"`,
          `"${getActionDescription(log.action, log.details)}"`,
          `"${JSON.stringify(log.details).replace(/"/g, '""')}"`
        ].join(','))
      ]
      
      const csv = csvRows.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Ошибка экспорта:', error)
    }
  }

  const handleCleanupLogs = async () => {
    if (window.confirm('Удалить логи старше 90 дней? Это действие нельзя отменить.')) {
      try {
        const response = await secureApiService.request('/audit/cleanup?days=90', { method: 'DELETE' })
        if (response.success) {
          alert(response.message || 'Старые логи успешно удалены')
          await loadLogs() // Обновляем список
        } else {
          alert('Ошибка при удалении логов')
        }
      } catch (error) {
        console.error('Ошибка очистки логов:', error)
        alert('Ошибка при удалении логов')
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Загрузка логов...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и действия */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Аудит и логи</h1>
          <p className="text-gray-600">История всех действий в системе</p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleCleanupLogs}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Очистить старые</span>
          </button>
          
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Экспорт CSV</span>
          </button>
        </div>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Всего записей</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <Calendar className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Сегодня</p>
              <p className="text-2xl font-bold text-gray-900">{stats.today}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <Clock className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-500">За неделю</p>
              <p className="text-2xl font-bold text-gray-900">{stats.thisWeek}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <Plus className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Создано</p>
              <p className="text-2xl font-bold text-gray-900">{stats.creates}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <Edit className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Обновлено</p>
              <p className="text-2xl font-bold text-gray-900">{stats.updates}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <Trash2 className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-500">Удалено</p>
              <p className="text-2xl font-bold text-gray-900">{stats.deletes}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск в логах..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Все действия</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Весь период</option>
            <option value="today">Сегодня</option>
            <option value="week">Последняя неделя</option>
            <option value="month">Последний месяц</option>
          </select>

          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Все пользователи</option>
            {uniqueUsers.map(user => (
              <option key={user} value={user}>{user}</option>
            ))}
          </select>

          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Filter className="w-4 h-4" />
            <span>Показано: {filteredLogs.length}</span>
          </div>
        </div>
      </div>

      {/* Список логов */}
      <div className="space-y-3">
        <AnimatePresence>
          {filteredLogs.map((log) => (
            <LogEntry
              key={log.id}
              log={log}
              onViewDetails={(log) => setDetailsModal({ isOpen: true, log })}
            />
          ))}
        </AnimatePresence>
      </div>

      {filteredLogs.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {logs.length === 0 ? 'Нет записей в логе' : 'Записи не найдены'}
          </h3>
          <p className="text-gray-500">
            {logs.length === 0 
              ? 'Логи будут появляться по мере работы с системой'
              : 'Попробуйте изменить параметры поиска или фильтры'
            }
          </p>
        </div>
      )}

      {/* Модальное окно с деталями */}
      <AnimatePresence>
        <LogDetailsModal
          log={detailsModal.log}
          isOpen={detailsModal.isOpen}
          onClose={() => setDetailsModal({ isOpen: false, log: null })}
        />
      </AnimatePresence>
    </div>
  )
}

export default AuditPage 